import { Card, Grid, CrudMode, IconButton, Tooltip, Typography, ValidationErrors, deepCopy, Stack } from "@compass/core";
import {
  Application,
  ApplicationEditResource,
  ApplicationSection,
  ApplicationViewResource,
  SimpleDto,
} from "generated/openApi";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import produce from "immer";
import { useEffect, useState } from "react";
import { createEntity } from "utils/CreateEntity";
import { DragDropContext, Draggable } from "react-beautiful-dnd";
import { MyDroppable } from "components/common/Draggable/Droppable";
import { reorder } from "utils/Reorder";
import { ApplicationSectionContainer } from "./ApplicationSectionContainer";

type ApplicationSectionsProps = {
  application: Application;
  copyApplication: number | undefined;
  setApplication: (value: Application) => void;
  refreshApplication: Application | undefined;
  viewResource: ApplicationViewResource;
  editResource: ApplicationEditResource | undefined;
  crudMode: CrudMode;
  validationErrors: ValidationErrors;
  navigateEditMode: () => void;
};

export const ApplicationSections = ({
  application,
  setApplication,
  refreshApplication,
  copyApplication,
  viewResource,
  editResource,
  crudMode,
  validationErrors,
  navigateEditMode,
}: ApplicationSectionsProps) => {
  // const [sections, setSections] = useState<ApplicationSection[]>(application.applicationSections!);
  const [selectedSection, setSelectedSection] = useState<ApplicationSection | null>();
  const [pages, setPages] = useState<SimpleDto[]>([{ id: 1, name: "1" }]);

  useEffect(() => {
    // init pages
    let tempPages = deepCopy(pages);

    application.applicationSections?.forEach((s) => {
      // if page index hasn't been set or somehow got to 0, set to 1
      if (!s.pageIndex || s.pageIndex == 0) {
        s.pageIndex = 1;
      } else {
        if (!tempPages.some((p) => p.id == s.pageIndex)) {
          tempPages.push({ id: s.pageIndex, name: s.pageIndex.toString() });
        }
      }
    });
    tempPages.push({ id: 0, name: "Add New Page" });
    setPages(tempPages);
  }, []);

  const addPage = (newPageNumber: number) => {
    let tempPages = deepCopy(pages);
    tempPages.push({ id: newPageNumber, name: newPageNumber.toString() });

    tempPages.sort((a, b) => {
      if (a.id === 0) return 1; // if a's id is 2, it should be considered greater
      if (b.id === 0) return -1; // if b's id is 2, it should be considered smaller
      return a.id - b.id; // otherwise, sort normally
    });

    setPages(tempPages);
  };

  const addSection = (displayOrder: number) => {
    setApplication(
      produce(application, (draft) => {
        let newSection: ApplicationSection = createEntity<ApplicationSection>({
          displayOrder,
        });

        // increase the display order of all questions after the newly added question
        var increasingQuestions = draft?.applicationSections?.filter((q) => q.displayOrder >= displayOrder);
        increasingQuestions?.forEach((q) => q.displayOrder++);

        draft?.applicationSections?.push(newSection);

        draft?.applicationSections?.sort((a, b) => a.displayOrder - b.displayOrder);

        setSelectedSection(newSection);
      })
    );
  };

  const updateSection = (section: ApplicationSection) => {
    setApplication(
      produce(application, (draft) => {
        let draftSectionIndex = draft?.applicationSections?.findIndex(
          (q) => q.id == section.id && q.displayOrder == section.displayOrder
        );

        draft!.applicationSections![draftSectionIndex!] = section;
      })
    );
  };

  const grid = 8;

  const getItemStyle = (padding, isDragging, draggableStyle) => ({
    // some basic styles to make the items look a bit nicer
    userSelect: "none",
    padding: grid * padding,
    margin: `0 0 ${grid}px 0`,

    // change background colour if dragging
    background: isDragging ? "lightgreen" : "white",

    // styles we need to apply on draggables
    ...draggableStyle,
  });

  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    // dropped outside the list
    if (!destination) {
      return;
    }
    const sID = source.droppableId;
    const dID = destination.droppableId;

    if (sID === dID) {
      // source droppable is the same as the destination, so it is moving within the same container
      // just need to reorder the items in the container

      const items = reorder<ApplicationSection>(application.applicationSections!, source.index, destination.index);

      setApplication(
        produce(application, (draft) => {
          draft!.applicationSections = items;
        })
      );
      // setSections(items);
    }
  };

  const renderSections = (section: ApplicationSection, index: number) => {
    return (
      <Draggable key={section.id} draggableId={section.id.toString()} index={index}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.draggableProps}
            style={getItemStyle(2, snapshot.isDragging, provided.draggableProps.style)}
          >
            <Grid container {...provided.dragHandleProps}>
              <ApplicationSectionContainer
                pages={pages}
                addPage={addPage}
                section={section}
                navigateEditMode={navigateEditMode}
                updateSection={updateSection}
                addSection={addSection}
                selected={selectedSection?.id == section.id && selectedSection?.displayOrder == section.displayOrder}
                setSelected={setSelectedSection}
                key={`${section.id}:${section.displayOrder}`}
              />
            </Grid>
          </div>
        )}
      </Draggable>
    );
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      
        <MyDroppable id="sections" type="sections">
          {application &&
            application.applicationSections?.filter((s) => !s.deleted).map((s, index) => renderSections(s, index))}
          {application && application.applicationSections?.length == 0 && (
            <Stack direction="column" spacing="md" py={2}>
              <Grid item xs={10} display="flex" alignItems="center" justifyContent="center">
                <Card sx={{ minWidth: "500px" }}>
                  <Typography>No sections yet, click to begin!</Typography>
                  <IconButton onClick={() => addSection(1)}>
                    <Tooltip title="Add New Section">
                      <AddCircleOutlineIcon />
                    </Tooltip>
                  </IconButton>
                </Card>
              </Grid>
            </Stack>
          )}
        </MyDroppable>
    </DragDropContext>
  );
};
